import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Link } from "react-router-dom";
import { authGet, authPatch } from "../../lib";
import {
  PREOWNED_PREFIX,
  PREOWNED_PT_FEATURES,
  PREOWNED_SITE_URL,
} from "../../themes";

/** status_id -> chip color. 1-3 are live, 4 sold, 5 disabled. */
const STATUS_COLOR = {
  1: "#2e7d32",
  2: "#ed6c02",
  3: "#0288d1",
  4: "#c62828",
  5: "#757575",
};

/**
 * Row height is driven by the inputs, not the cells — a row is as tall as its
 * tallest child. 24px keeps every control comfortably inside the 35px cell.
 * Inline because MUI's outlined-dense padding is otherwise hard to outrank.
 */
const INPUT_BOX = { style: { height: 24 } };
/**
 * Opens the menu below the field instead of over it. MUI v4 ignores a vertical
 * anchorOrigin unless getContentAnchorEl is explicitly null.
 */
const DROP_BELOW = {
  getContentAnchorEl: null,
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
};

/** Inline because MUI's MenuItem sets its own font-size from the theme. */
const MENU_ITEM = { fontSize: "0.8rem", minHeight: 30 };

/**
 * Storefront URL, mirroring the CI4 WatchModel::getUrl():
 *   /preowned/{brand}-{series}-{model_no}-{stock_id}
 * `/`, `#` and space are swapped for `~`, backtick and `_` first; brand and
 * series then collapse anything else to dashes. model_no is deliberately NOT
 * dash-collapsed — the PHP leaves it encoded only.
 */
const encodeModelNo = (v) =>
  String(v ?? "").replace(/\//g, "~").replace(/#/g, "`").replace(/ /g, "_");

const slugPart = (v) =>
  encodeURIComponent(
    encodeModelNo(v).replace(/[^A-Za-z0-9 ]/g, "-").replace(/-+/g, "-"),
  );

const storefrontUrl = (r) =>
  !PREOWNED_SITE_URL || !r.stock_id
    ? null
    : `${PREOWNED_SITE_URL}/${slugPart(r.brand)}-${slugPart(r.series)}-` +
      `${encodeModelNo(r.model_no)}-${r.stock_id}`;

/** Prices are whole dollars — nothing here is sold in cents. */
const NUMERIC_FIELDS = ["selling_price", "special_price"];
const whole = (v) =>
  v == null || v === "" || !Number.isFinite(Number(v))
    ? ""
    : String(Math.round(Number(v)));
const INPUT_TEXT = { padding: "2px 8px", fontSize: "0.8rem" };

/** NULL and 0 are equivalent for these, so toggling twice isn't a change. */
const BOOL_FIELDS = ["has_multiple_units", "on_special", "sync_wf", "sync_ws"];

/** Live statuses, preselected on load. Stable ids — see item_status. */
const DEFAULT_STATUS_IDS = [1, 2, 3];

/**
 * Widths are explicit and the table is `table-layout: fixed`, so the header
 * lays out at full size before any rows arrive — otherwise the table renders
 * squished to its headers and visibly jumps wider once data loads.
 */
const COLUMNS = [
  { key: "stock_id", label: "Stock ID", sortable: true, width: 92 },
  { key: "brand", label: "Brand", sortable: true, width: 150 },
  { key: "model_no", label: "Model", sortable: true, width: 227 },
  { key: "status", label: "Status", sortable: true, width: 168 },
  { key: "has_multiple_units", label: "Multi", align: "center", width: 62 },
  { key: "selling_price", label: "Price", sortable: true, width: 108 },
  { key: "posted_date", label: "Available", sortable: true, width: 152 },
  { key: "on_special", label: "On Special", align: "center", width: 74 },
  { key: "special_price", label: "Special Price", width: 108 },
  // Watchfinder / WatchSource are PT sales channels; TPC runs the same build
  // with REACT_APP_PREOWNED_PT_FEATURES=false and never sees these.
  { key: "sync_wf", label: "WF", align: "center", ptOnly: true, width: 58 },
  { key: "sync_ws", label: "WS", align: "center", ptOnly: true, width: 58 },
].filter((c) => !c.ptOnly || PREOWNED_PT_FEATURES);

/** Fixed layout needs a total to size against; TableContainer scrolls below it. */
const TABLE_WIDTH = COLUMNS.reduce((n, c) => n + c.width, 0);

const useStyles = makeStyles((theme) => ({
  title: { marginBottom: theme.spacing(1.5) },
  controls: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  saveArea: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginLeft: theme.spacing(2),
  },
  notice: { color: theme.palette.success.main },
  /**
   * No overflow here, so the header's `position: sticky` anchors to the page
   * and the whole page scrolls. Any overflow value other than `visible` would
   * make this the scrollport instead — and since overflow-x:auto forces
   * overflow-y:auto, horizontal scrolling and page-sticky are mutually
   * exclusive. Below the table's own width there's no choice, so it falls back
   * to scrolling internally.
   */
  tableWrap: {
    overflow: "visible",
    [theme.breakpoints.down(1560)]: { overflowX: "auto" },
  },
  right: {
    marginLeft: "auto",
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  narrow: { width: 150 },
  statusItem: { marginRight: theme.spacing(1) },
  statusLabel: { display: "flex", alignItems: "center", fontSize: "0.82rem" },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    marginRight: 6,
    display: "inline-block",
  },
  search: { width: 240 },
  link: {
    color: "#1565c0",
    fontWeight: 500,
    textDecoration: "none",
    "&:hover": { color: "#0d47a1", textDecoration: "underline" },
  },
  chip: { color: "#fff", height: 20, fontSize: "0.7rem" },
  dirty: { backgroundColor: "rgba(224,160,48,0.16)" },
  // Inputs in every cell push the default row past 50px. The height comes from
  // MuiOutlinedInput-inputMarginDense (10.5px top+bottom), so capping the input
  // root height is what actually shrinks the row; element-qualified selectors
  // keep these above MUI's own single-class rules.
  /**
   * Order matters: nth-of-type outranks MUI's own hover and selected rules, so
   * these are declared after the stripe and win the tie by source order.
   * Dirty (selected) beats hover, so a pending row stays amber under the cursor.
   */
  zebra: {
    "& tbody tr:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.013)" },
    '& tbody tr[class*="MuiTableRow-hover"]:hover': {
      backgroundColor: "rgba(21,101,192,0.13)",
    },
    "& tbody tr.Mui-selected, & tbody tr.Mui-selected:hover": {
      backgroundColor: "rgba(224,160,48,0.13)",
    },
  },
  /**
   * MUI generates suffixed class names here (MuiTableCell-sizeSmall-275), so
   * plain `.MuiTableCell-root` selectors never match — the same reason
   * AddWatchPage.styles.js uses [class*=...] throughout. Both forms are listed
   * so this holds whichever way the class names come out.
   */
  compact: {
    // MUI's default 24px right padding is for sort icons and is wasteful in a
    // dense grid; 8px gives narrow columns real room back.
    '& td[class*="MuiTableCell-sizeSmall"], & th[class*="MuiTableCell-sizeSmall"], & td.MuiTableCell-sizeSmall, & th.MuiTableCell-sizeSmall':
      {
        padding: "2px 8px",
        lineHeight: 1.2,
      },
    // Body cells clip; a wrapped one would set the row height on its own.
    '& td[class*="MuiTableCell-sizeSmall"], & td.MuiTableCell-sizeSmall': {
      height: 35,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    // Headers may wrap to two lines, which is what lets narrow columns keep a
    // readable label without widening the column.
    '& th[class*="MuiTableCell-sizeSmall"], & th.MuiTableCell-sizeSmall': {
      // Its own padding — the shared rule above sets the body's tight 2px,
      // which reads as cramped on a header sitting hard against the rows.
      padding: "7px 8px",
      whiteSpace: "normal",
      verticalAlign: "bottom",
      position: "sticky",
      top: 0,
      zIndex: 2,
      backgroundColor: "#fff",
      boxShadow: "inset 0 -1px 0 rgba(224,224,224,1)",
    },
    '& [class*="MuiFormControl-root"]': { margin: 0, verticalAlign: "middle" },
    '& [class*="MuiOutlinedInput-root"]': { height: 24 },
    '& [class*="MuiOutlinedInput-input"]': {
      paddingTop: 3,
      paddingBottom: 3,
      fontSize: "0.8rem",
    },
    '& [class*="MuiCheckbox-root"]': { padding: 1 },
  },
  numInput: { width: 84 },
  dateInput: { width: 132 },
  statusInput: { width: 148 },
  menu: {
    "& .MuiMenuItem-root": { fontSize: "0.8rem", minHeight: 30 },
  },
  statusValue: {
    display: "flex",
    alignItems: "center",
    fontSize: "0.8rem",
  },
}));

/** Current display value: the pending edit if there is one, else the row. */
const valueOf = (row, rowEdits, field) =>
  rowEdits && field in rowEdits ? rowEdits[field] : row[field];

/** Row value for a price input; leaves in-progress typing untouched. */
const priceOf = (row, rowEdits, field) =>
  rowEdits && field in rowEdits ? rowEdits[field] : whole(row[field]);

/**
 * Memoized so editing one row doesn't re-render the other 199. `rowEdits` is
 * undefined for untouched rows, which keeps their props referentially stable.
 */
const WatchRow = React.memo(function WatchRow({
  r,
  rowEdits,
  statuses,
  statusById,
  classes,
  setField,
}) {
  const rowDirty = !!rowEdits;
  const cell = (field) =>
    rowEdits && field in rowEdits ? classes.dirty : undefined;
  return (
              <TableRow key={r.item_id} hover selected={rowDirty}>
                <TableCell>
                  {storefrontUrl(r) ? (
                    <a
                      href={storefrontUrl(r)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.link}
                      title="View on the pre-owned site"
                    >
                      {r.stock_id}
                    </a>
                  ) : (
                    r.stock_id || r.item_id
                  )}
                </TableCell>
                <TableCell>{r.brand}</TableCell>
                <TableCell>
                  <Link
                    to={`${PREOWNED_PREFIX}/${
                      r.accessory_type_id != null ? "edit-accessory" : "edit-watch"
                    }/${r.item_id}`}
                    className={classes.link}
                    title="Edit this watch"
                  >
                    {r.model_no}
                  </Link>
                </TableCell>
                <TableCell className={cell("status_id")}>
                  <TextField
                    select
                    value={valueOf(r, rowEdits, "status_id") ?? ""}
                    onChange={(e) => setField(r, "status_id", e.target.value)}
                    className={classes.statusInput}
                    InputProps={INPUT_BOX}
                    SelectProps={{
                      MenuProps: {
                        ...DROP_BELOW,
                        classes: { paper: classes.menu },
                        // The 26px field is centred in a 35px cell, so its
                        // anchor sits 6px below where the taller filter's does.
                        PaperProps: { style: { marginTop: -6 } },
                      },
                      renderValue: (v) => (
                        <span className={classes.statusValue}>
                          <span
                            className={classes.dot}
                            style={{ backgroundColor: STATUS_COLOR[v] }}
                          />
                          {statusById[v] ?? ""}
                        </span>
                      ),
                    }}
                  >
                    {statuses.map((st) => (
                      <MenuItem
                        key={st.status_id}
                        value={st.status_id}
                        style={MENU_ITEM}
                      >
                        <span
                          className={classes.dot}
                          style={{ backgroundColor: STATUS_COLOR[st.status_id] }}
                        />
                        {st.status}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell align="center" className={cell("has_multiple_units")}>
                  <Checkbox
                    size="small"
                    color="primary"
                    style={{ padding: 2 }}
                    checked={!!valueOf(r, rowEdits, "has_multiple_units")}
                    onChange={(e) =>
                      setField(r, "has_multiple_units", e.target.checked ? 1 : 0)
                    }
                  />
                </TableCell>
                <TableCell className={cell("selling_price")}>
                  <TextField
                    value={priceOf(r, rowEdits, "selling_price")}
                    onChange={(e) => setField(r, "selling_price", e.target.value)}
                    className={classes.numInput}
                    InputProps={INPUT_BOX}
                    inputProps={{ style: INPUT_TEXT }}
                  />
                </TableCell>
                <TableCell className={cell("posted_date")}>
                  <TextField
                    type="date"
                    value={valueOf(r, rowEdits, "posted_date") ?? ""}
                    onChange={(e) => setField(r, "posted_date", e.target.value)}
                    className={classes.dateInput}
                    InputProps={INPUT_BOX}
                    inputProps={{ style: INPUT_TEXT }}
                  />
                </TableCell>
                <TableCell align="center" className={cell("on_special")}>
                  <Checkbox
                    size="small"
                    color="primary"
                    style={{ padding: 2 }}
                    checked={!!valueOf(r, rowEdits, "on_special")}
                    onChange={(e) =>
                      setField(r, "on_special", e.target.checked ? 1 : 0)
                    }
                  />
                </TableCell>
                <TableCell align="right" className={cell("special_price")}>
                  <TextField
                    value={priceOf(r, rowEdits, "special_price")}
                    onChange={(e) => setField(r, "special_price", e.target.value)}
                    className={classes.numInput}
                    InputProps={INPUT_BOX}
                    inputProps={{ style: INPUT_TEXT }}
                  />
                </TableCell>
                {PREOWNED_PT_FEATURES && (
                  <>
                    <TableCell align="center" className={cell("sync_wf")}>
                      <Checkbox
                        size="small"
                        color="primary"
                        style={{ padding: 2 }}
                        checked={!!valueOf(r, rowEdits, "sync_wf")}
                        onChange={(e) =>
                          setField(r, "sync_wf", e.target.checked ? 1 : 0)
                        }
                      />
                    </TableCell>
                    <TableCell align="center" className={cell("sync_ws")}>
                      <Checkbox
                        size="small"
                        color="primary"
                        style={{ padding: 2 }}
                        checked={!!valueOf(r, rowEdits, "sync_ws")}
                        onChange={(e) =>
                          setField(r, "sync_ws", e.target.checked ? 1 : 0)
                        }
                      />
                    </TableCell>
                  </>
                )}
              </TableRow>
              );
});

export function PreownedWatchesPage(props) {
  const classes = useStyles();
  // Sidebar search navigates here with ?search=, so seed from the URL.
  const initialSearch = new URLSearchParams(
    props?.pageRoute?.location?.search || window.location.search,
  ).get("search") || "";

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(200);
  const [search, setSearch] = useState(initialSearch);
  const [kind, setKind] = useState("watches");
  const [statusIds, setStatusIds] = useState(DEFAULT_STATUS_IDS);
  const [statuses, setStatuses] = useState([]);
  const [sort, setSort] = useState("posted_date");
  const [order, setOrder] = useState("desc");
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await authGet("/preowned/attributes/status");
      setStatuses(data?.rows ?? []);
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const qs = new URLSearchParams({
      page,
      page_size: pageSize,
      kind,
      sort,
      order,
    });
    if (search) qs.set("search", search);
    // No selection means no filter rather than an empty grid.
    if (statusIds.length) qs.set("status_id", statusIds.join(","));

    const { data, error: err } = await authGet(`/preowned/watches?${qs}`);
    if (err) setError(err.message || "Failed to load");
    else {
      setRows(data?.rows ?? []);
      setTotal(data?.total ?? 0);
    }
    setLoading(false);
  }, [page, pageSize, search, kind, statusIds, sort, order]);

  useEffect(() => {
    // Debounced so typing in the search box doesn't fire a query per keystroke.
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Clear the save confirmation on its own; editing again clears it too.
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(""), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  const sortBy = (key) => {
    if (sort === key) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setSort(key);
      setOrder("asc");
    }
    setPage(0);
  };

  const dirtyCount = Object.keys(edits).length;
  const statusById = useMemo(
    () => Object.fromEntries(statuses.map((st) => [st.status_id, st.status])),
    [statuses],
  );



  const setField = useCallback((row, field, value) => {
    setNotice("");
    setEdits((prev) => {
      const next = { ...(prev[row.item_id] || {}), [field]: value };
      // Dropping back to the original value un-dirties the field, so Save
      // never reports changes the user has already undone. NULL and 0 are the
      // same thing for the checkboxes — sync_wf is NULL on most rows.
      const orig = BOOL_FIELDS.includes(field)
        ? row[field]
          ? 1
          : 0
        : row[field];
      const same = NUMERIC_FIELDS.includes(field)
        ? Number(next[field] || 0) === Number(orig || 0)
        : String(next[field] ?? "") === String(orig ?? "");
      if (same) delete next[field];
      const out = { ...prev };
      if (Object.keys(next).length) out[row.item_id] = next;
      else delete out[row.item_id];
      return out;
    });
  }, []);

  async function save() {
    setSaving(true);
    setError("");
    setNotice("");
    const changes = Object.entries(edits).map(([item_id, fields]) => ({
      item_id: Number(item_id),
      fields,
    }));
    const { data, error: err } = await authPatch("/preowned/watches", { changes });
    setSaving(false);
    if (err) return setError(err.message || "Save failed");
    setEdits({});
    const n = data?.updated ?? 0;
    setNotice(`Saved ${n} record${n === 1 ? "" : "s"}.`);
    load();
  }

  const toggleStatus = (id) => {
    setStatusIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
    setPage(0);
  };

  const reset = (fn) => (e) => {
    fn(e.target.value);
    setPage(0);
  };

  return (
    <Box p={3}>
      <Typography variant="h5" className={classes.title}>
        Pre-Owned Watches
        <Typography component="span" variant="body2" color="textSecondary">
          {"  "}
          {loading
            ? "…"
            : `${total.toLocaleString()} ${
                kind === "accessories"
                  ? "accessories"
                  : kind === "watches"
                  ? "watches"
                  : "records"
              }`}
        </Typography>
      </Typography>

      <div className={classes.controls}>
        {statuses.map((s) => (
          <FormControlLabel
            key={s.status_id}
            className={classes.statusItem}
            control={
              <Checkbox
                size="small"
                color="primary"
                checked={statusIds.includes(s.status_id)}
                onChange={() => toggleStatus(s.status_id)}
              />
            }
            label={
              <span className={classes.statusLabel}>
                <span
                  className={classes.dot}
                  style={{ backgroundColor: STATUS_COLOR[s.status_id] }}
                />
                {s.status}
              </span>
            }
          />
        ))}

        <div className={classes.saveArea}>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            disabled={!dirtyCount || saving}
            onClick={save}
          >
            {/* Counts records, not fields — matches the highlighted rows. Stays
                neutral because pending edits survive a change of Type filter. */}
            {saving
              ? "Saving…"
              : dirtyCount
              ? `Save ${dirtyCount} record${dirtyCount === 1 ? "" : "s"}`
              : "Save"}
          </Button>
          {!!dirtyCount && (
            <Button size="small" onClick={() => setEdits({})} disabled={saving}>
              Discard
            </Button>
          )}
          {notice && (
            <Typography variant="body2" className={classes.notice}>
              {notice}
            </Typography>
          )}
        </div>

        <div className={classes.right}>
          <TextField
            className={classes.search}
            placeholder="Search stock ID, model, brand…"
            value={search}
            onChange={reset(setSearch)}
          />
          <TextField
            select
            label="Type"
            className={classes.narrow}
            value={kind}
            onChange={reset(setKind)}
            SelectProps={{ MenuProps: { ...DROP_BELOW, classes: { paper: classes.menu } } }}
          >
            <MenuItem style={MENU_ITEM} value="watches">Watches</MenuItem>
            <MenuItem style={MENU_ITEM} value="accessories">
              Accessories
            </MenuItem>
            <MenuItem style={MENU_ITEM} value="all">All</MenuItem>
          </TextField>
        </div>
      </div>

      {error && (
        <Typography color="error" gutterBottom>
          {error}
        </Typography>
      )}

      <TableContainer component={Paper} className={classes.tableWrap}>
        {loading && <LinearProgress color="secondary" style={{ height: 2 }} />}
        <Table
          stickyHeader
          size="small"
          className={`${classes.compact} ${classes.zebra}`}
          style={{
            opacity: loading && rows.length ? 0.55 : 1,
            tableLayout: "fixed",
            minWidth: TABLE_WIDTH,
          }}
        >
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableCell
                  key={c.key}
                  align={c.align || "left"}
                  style={{ width: c.width }}
                >
                  {c.sortable ? (
                    <TableSortLabel
                      active={sort === c.key}
                      direction={sort === c.key ? order : "asc"}
                      onClick={() => sortBy(c.key)}
                    >
                      {c.label}
                    </TableSortLabel>
                  ) : (
                    c.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <WatchRow
                key={r.item_id}
                r={r}
                rowEdits={edits[r.item_id]}
                statuses={statuses}
                statusById={statusById}
                classes={classes}
                setField={setField}
              />
            ))}
            {loading && !rows.length && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} style={{ height: 220 }}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gridGap={12}
                  >
                    <CircularProgress size={28} color="secondary" />
                    <Typography variant="body2" color="textSecondary">
                      Loading pre-owned watches…
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {!rows.length && !loading && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length}>
                  <Typography variant="body2" color="textSecondary">
                    No items match.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onChangePage={(e, p) => setPage(p)}
        rowsPerPage={pageSize}
        onChangeRowsPerPage={(e) => {
          setPageSize(Number(e.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100, 200]}
      />
    </Box>
  );
}
export default PreownedWatchesPage;